/* eslint-disable react/prop-types */
import { useEffect, useState } from "react";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import useApi from "components/Utils/api";
import toast from "utils/toast";

// const DOCS_URL = "https://youcloud-paytech.docs.buildwithfern.com/you-cloud-payment-gateway-internal";
const DOCS_URL = "https://documenter.getpostman.com/view/8928586/2sBXwwmShc";

function SectionHeader({ title, description }) {
  return (
    <MDBox mb={2}>
      <MDTypography variant="h6" fontWeight="bold">
        {title}
      </MDTypography>
      <MDTypography variant="body2" color="text" sx={{ mt: 0.25 }}>
        {description}
      </MDTypography>
      <Divider sx={{ mt: 1.5, mb: 2 }} />
    </MDBox>
  );
}

function SecretField({ label, value }) {
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (!value) return;
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <TextField
      label={label}
      fullWidth
      size="small"
      value={value ?? ""}
      type={visible ? "text" : "password"}
      InputProps={{
        readOnly: true,
        endAdornment: (
          <InputAdornment position="end">
            <Tooltip title={visible ? "Hide" : "Show"}>
              <IconButton size="small" onClick={() => setVisible((v) => !v)} edge="end">
                {visible ? (
                  <VisibilityOffIcon fontSize="small" />
                ) : (
                  <VisibilityIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
            <Tooltip title={copied ? "Copied!" : "Copy"}>
              <IconButton size="small" onClick={handleCopy} edge="end" sx={{ ml: 0.5 }}>
                <ContentCopyIcon fontSize="small" sx={{ color: copied ? "#4caf50" : undefined }} />
              </IconButton>
            </Tooltip>
          </InputAdornment>
        ),
      }}
      sx={{ mb: 2 }}
    />
  );
}

const isValidCallbackUrl = (value) => {
  if (!value) return true; // empty clears the callback URL
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch (e) {
    return false;
  }
};

function DeveloperSettings() {
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [gatewayMerchant, setGatewayMerchant] = useState(null);
  const [callbackUrl, setCallbackUrl] = useState("");
  const [callbackError, setCallbackError] = useState(false);
  const [saving, setSaving] = useState(false);
  const api = useApi();

  useEffect(() => {
    api
      .get("merchant/account-info")
      .then((res) => {
        const body = res.data?.body ?? {};
        setPaymentInfo(body.paymentInfo ?? null);
        setGatewayMerchant(body.gatewayMerchant ?? null);
        setCallbackUrl(body.gatewayMerchant?.callBackUrl ?? "");
      })
      .catch((err) => {
        if (err.response?.status === 403) {
          toast.error("You do not have permission to perform this action.");
          return;
        }
        toast.error("Failed to load developer settings.");
      });
  }, []); // eslint-disable-line

  const handleSaveCallbackUrl = () => {
    const trimmed = callbackUrl.trim();
    if (!isValidCallbackUrl(trimmed)) {
      setCallbackError(true);
      return;
    }
    setCallbackError(false);
    setSaving(true);
    api
      .put("merchant/account-info", { callbackUrl: trimmed })
      .then(() => {
        toast.success("Callback URL saved.");
        setGatewayMerchant((prev) => (prev ? { ...prev, callBackUrl: trimmed } : prev));
      })
      .catch((err) => {
        if (err.response?.status === 403) {
          toast.error("You do not have permission to perform this action.");
          return;
        }
        toast.error("Failed to save callback URL.");
      })
      .finally(() => setSaving(false));
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={6} mb={3}>
        <MDBox mb={3}>
          <MDTypography variant="h5" fontWeight="bold">
            Developer Settings
          </MDTypography>
        </MDBox>

        {/* ── Payment Info ── */}
        <Card sx={{ p: 3, mb: 3 }}>
          <SectionHeader
            title="Payment Info"
            description="Your integration credentials. Keep these secure."
          />

          <SecretField label="Client ID" value={paymentInfo?.clientId} />
          <SecretField label="Client Secret" value={paymentInfo?.clientSecret} />

          <MDBox mt={1}>
            <MDButton
              variant="gradient"
              color="info"
              startIcon={<OpenInNewIcon />}
              onClick={() => window.open(DOCS_URL, "_blank", "noopener,noreferrer")}
            >
              Open Documentation
            </MDButton>
          </MDBox>
        </Card>

        {/* ── Account Settings ── */}
        <Card sx={{ p: 3 }}>
          <SectionHeader
            title="Account Settings"
            description="Configure your account preferences."
          />

          <TextField
            label="Callback URL"
            fullWidth
            size="small"
            value={callbackUrl}
            onChange={(e) => {
              setCallbackUrl(e.target.value);
              if (callbackError) setCallbackError(false);
            }}
            placeholder="https://yoursite.com/payment/callback"
            error={callbackError}
            helperText={
              callbackError
                ? "Enter a valid http(s) URL, or leave empty to clear the callback URL."
                : "We will POST payment notifications to this URL."
            }
            sx={{ mb: 2 }}
          />
          <MDBox>
            <MDButton
              variant="gradient"
              color="info"
              onClick={handleSaveCallbackUrl}
              disabled={saving || (gatewayMerchant?.callBackUrl ?? "") === callbackUrl.trim()}
            >
              {saving ? "Saving…" : "Save"}
            </MDButton>
          </MDBox>
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default DeveloperSettings;
